import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Button, DialogTitle, DialogActions, DialogContent, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import PageviewIcon from '@mui/icons-material/Pageview';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import LoopIcon from '@mui/icons-material/Loop';
import BrowserUpdatedIcon from '@mui/icons-material/BrowserUpdated'; 
import { LoadingButton } from '@mui/lab';
import Iconify from '../iconify/Iconify';

ConfirmDialog.propTypes = {
  title: PropTypes.node,
  action: PropTypes.func,
  cancelAction: PropTypes.func,
  content: PropTypes.node,
  icon: PropTypes.string,
  color: PropTypes.string,
  actionButton: PropTypes.string,
  tooltip: PropTypes.string,
  button: PropTypes.string,
  disabled: PropTypes.bool,
  dialogOnly: PropTypes.bool,
  noCancelButton: PropTypes.bool,
};

export default function ConfirmDialog({
  button,
  title = "Delete",
  content,
  action,
  cancelAction = () => null,
  icon = "delete-outline",
  color = "error",
  tooltip,
  actionButton = "Delete",
  disabled = false,
  dialogOnly = false,
  noCancelButton = false,
  ...other
}) {
  const [openConfirm, setOpenConfirm] = useState(dialogOnly);
  const [submitting, setSubmitting] = useState(false);

  const handleOpenConfirm = (e) => {
    e.preventDefault();
    e.confirmDialog = true;
    setOpenConfirm(true);
    e.stopPropagation();
  };

  const handleCloseConfirm = () => {
    cancelAction();
    setOpenConfirm(false);
  };
  const handleAction = () => {
    setSubmitting(true);
    action()?.finally(() => {
      setOpenConfirm(false);
      setSubmitting(false);
    });
  };

  const getIcon = useMemo(() => {
    if (icon === 'delete-outline') {
      return <DeleteOutlineIcon />;
    }
    if (icon === 'refresh') {
      return <LoopIcon />;
    }
    if (icon === 'email-search') {
      return <PageviewIcon />;
    }
    if (icon === 'browser-updated') {
      return <BrowserUpdatedIcon />;
    }
    if (icon === 'delete-sweep') {
      return <DeleteSweepOutlinedIcon />;
    }
    if (icon === 'history-outline') {
      return <HistoryIcon />
    }
    return <Iconify icon={icon} />;
  }, [icon]);

  return <>
    {!dialogOnly && <>
      {button ? <Button variant={button} size="small" color={color} startIcon={getIcon} onClick={handleOpenConfirm} disabled={disabled} {...other}>
        <Typography>{actionButton}</Typography>
      </Button> :
        <Tooltip title={tooltip}>
          <IconButton color={color} onClick={handleOpenConfirm} disabled={disabled}>
            {getIcon}
          </IconButton>
        </Tooltip>}
    </>}
    <Dialog fullWidth maxWidth="xs" open={openConfirm} onClose={handleCloseConfirm} {...other}>
      <DialogTitle sx={{ pb: 2 }}>{title}</DialogTitle>
      {content && <DialogContent sx={{ typography: 'body2' }}> {content} </DialogContent>}
      <DialogActions>
        <LoadingButton loading={submitting} variant="contained" color={icon === 'history-outline' ? 'primary' : color} onClick={handleAction}>
          <Typography>{actionButton}</Typography>
        </LoadingButton>

        {!noCancelButton && <Button variant="outlined" color="inherit" onClick={handleCloseConfirm}>
          Cancel
        </Button>}
      </DialogActions>
    </Dialog>
  </>;
}
